import { PieChart } from '@mui/x-charts/PieChart';
import styles from './statusPieChart.module.css';
import React from 'react';

type AnchorX = 'left' | 'middle' | 'right';
type AnchorY = 'top' | 'middle' | 'bottom';
type Direction = 'row' | 'column';
type TextTransform = 'uppercase' | 'capitalize' | 'none';

export interface MetricsItem {
  status: string;
  count: number;
  revenue: number; // Changed from 'revenues' to 'revenue' to match your data
}

export interface StatusPieChartProps {
  metrics: MetricsItem[];
}

const pieChartProps = {
  height: 250,
  margin: { top: 20, right: 20, bottom: 60, left: 20 },
  series: [
    {
      innerRadius: 40,
      cornerRadius: 5,
      paddingAngle: 5,
    },
  ],
  slotProps: {
    legend: {
      direction: 'row' as Direction,
      position: {
        vertical: 'bottom' as AnchorY,
        horizontal: 'middle' as AnchorX,
      },
      hidden: false,
      padding: { top: 20, bottom: 0, left: 0, right: 0 },
      labelStyle: {
        fontSize: 12,
        textTransform: 'capitalize' as TextTransform,
      },
      itemMarkWidth: 10,
      itemMarkHeight: 10,
      markGap: 5,
      itemGap: 10,
    },
  },
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'modelled':
      return '#093fc3';
    case 'revised':
      return '#BBBCBE';
    case 'override':
      return '#FB923C';
    case 'supplier_input':
      return '#22C55E';
    case 'legacy':
      return '#8B5CF6';
    case 'estimated':
      return '#F59E0B';
    default:
      return '#093fc3';
  }
};

const StatusPieChart: React.FC<StatusPieChartProps> = ({ metrics }) => {
  // Filter out entries with zero count to avoid white spaces
  const filteredMetrics = metrics.filter(item => item.count > 0);

  // If no data after filtering, show empty state
  if (filteredMetrics.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '250px',
          color: '#666',
          fontSize: '14px',
        }}
      >
        No data available
      </div>
    );
  }

  const pieData = filteredMetrics.map((res, index) => {
    return {
      id: index,
      label: res.status,
      value: res.count,
      color: getStatusColor(res.status),
    };
  });

  return (
    <PieChart
      {...pieChartProps}
      series={[
        {
          ...pieChartProps.series[0],
          data: pieData,
        },
      ]}
      tooltip={{
        trigger: 'item',
        itemContent: props => {
          const { series, itemData } = props;
          if (!itemData) return null;

          // Get the original metric data using the filtered index
          const originalMetric = filteredMetrics[itemData.dataIndex];

          return (
            <div className={styles.toolTip}>
              <h3 className={styles.contentDetailsKey}>{series.data[itemData.dataIndex].label}</h3>
              <div className={styles.toolTipContent}>
                <h3 className={styles.contentDetailsKey}>Counts:</h3>
                <h3 className={styles.contentDetailsValue}>
                  {series.data[itemData.dataIndex].value}
                </h3>
              </div>
              <div className={styles.toolTipContent}>
                <h3 className={styles.contentDetailsKey}>Revenue:</h3>
                <h3 className={styles.contentDetailsValue}>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(originalMetric?.revenue || 0)}
                </h3>
              </div>
            </div>
          );
        },
      }}
    />
  );
};

export default StatusPieChart;
